import {CBOR} from 'cbor-redux';
import {Ulid} from 'id128';

import {LumaAnimation, type Projector} from '../../../types';
import {convertHexStringToNumArray} from '../../../utils';
import {convertRgbArrayToHex} from '../../../utils/colors';
import {ScratchpadType} from '../Scratchpad';

type Metadata = Projector<any>['meta'];

interface SerializeOptions {
  keys?: 'string' | 'numeric';
}
type ParseOptions = SerializeOptions & {fallback?: Projector<never>};

const ulidKeys = ['moodUlid', 'originalMoodUlid', 'heatProfileUlid'] as const;
type UlidKey = (typeof ulidKeys)[number];

const isUlidKey = (key: string) => ulidKeys.includes(key as UlidKey);

export class EvaluatedProjector {
  constructor(private projector: Projector<never>) {}

  get value() {
    return this.projector;
  }

  public addMeta(meta: ScratchpadType) {
    this.projector = {
      ...this.projector,
      meta: {
        ...this.projector.meta,
        ...meta,
      },
    };

    return this;
  }

  public static parse(
    buffer: ArrayBuffer,
    options: ParseOptions = {keys: 'string'},
  ): EvaluatedProjector {
    const decoded = EvaluatedProjector.tryDecodeBuffer(
      buffer,
      options.fallback,
    );

    const normalised = (decoded.meta as Metadata) ?? {};

    const meta = Object.entries(normalised).reduce<Metadata>(
      (obj, [key, value]) => {
        if (isUlidKey(key)) obj[key] = Ulid.construct(value).toCanonical();
        else if (key === 'userColors' || key === 'colors')
          obj[key] = value.map((color: number[]) =>
            convertRgbArrayToHex(color),
          );
        else obj[key] = value;

        return obj;
      },
      {},
    );

    return new EvaluatedProjector({...decoded, meta});
  }

  /**
   * @param options Keys formatting options. 'numeric' for toad devices, 'string' for peach devices
   * @returns the CBOR encoded projector
   */
  public serialize(_: SerializeOptions = {keys: 'string'}) {
    // if (options.keys === 'numeric') {
    //   // TODO
    // }

    const meta = Object.entries(this.projector.meta).reduce<Metadata>(
      (obj, [key, value]) => {
        if (isUlidKey(key)) obj[key] = Ulid.fromCanonical(value).bytes;
        else if (key === 'userColors' || key === 'colors')
          obj[key] = value.map(
            (c: string) => new Uint8Array(convertHexStringToNumArray(c)),
          );
        else obj[key] = value;

        return obj;
      },
      {},
    );

    return CBOR.encode({...this.projector, meta});
  }

  private static tryDecodeBuffer(
    buffer: ArrayBuffer,
    fallback?: Projector<never>,
  ) {
    // Buffer might be corrupted
    try {
      const decoded = CBOR.decode(buffer);

      return decoded;
    } catch (error) {
      console.error(error);

      return fallback;
    }
  }

  public isPartyModeProjector() {
    return !!(
      this.projector.meta?.tag === 'pikaled2-classic-disco-mood-light' &&
      this.projector.meta?.name === 'Party Mode'
    );
  }

  public isSingleColorSolidProjector() {
    return !!(
      this.projector.lamp.name === 'solid' && this.projector.lamp.param.color
    );
  }

  public isSingleColorPikaProjector() {
    return !!(
      this.projector.lamp.name === 'pikaled2' &&
      this.projector.meta?.userColors?.length === 1 &&
      this.projector.lamp.param.anim &&
      [
        LumaAnimation.STEADY,
        LumaAnimation.BREATHING,
        LumaAnimation.CIRCLING_SLOW,
      ].includes(this.projector.lamp.param.anim) &&
      this.projector.meta?.moodUlid === undefined
    );
  }
}
